import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isWithinInterval, parseISO } from "date-fns";

export interface UpcomingEvent {
  id: string;
  title: string;
  date: Date;
  time?: string;
  type: 'document' | 'driver_license' | 'maintenance' | 'ticket';
  priority: 'high' | 'medium' | 'low';
  source: string;
  reference_id: string;
  icon?: string;
}

export function useUpcomingEvents(companyId?: string) {
  return useQuery({
    queryKey: ["upcoming-events", companyId],
    queryFn: async (): Promise<UpcomingEvent[]> => {
      if (!companyId) return [];
      
      const now = new Date();
      const thirtyDaysFromNow = addDays(now, 30);
      const events: UpcomingEvent[] = [];

      try {
        // NoteONE certificates removed - system discontinued

        // 2. Documentos da frota vencendo - veículos
        const { data: vehicleDocuments } = await supabase
          .from("fleet_documents")
          .select(`
            id, 
            name, 
            expiry_date,
            vehicle_id,
            fleet_vehicles!inner(plate)
          `)
          .eq("company_id", companyId)
          .eq("status", "active")
          .eq("category", "vehicle")
          .not("expiry_date", "is", null);

        vehicleDocuments?.forEach((doc: any) => {
          if (doc.expiry_date) {
            const expiryDate = parseISO(doc.expiry_date);
            if (isWithinInterval(expiryDate, { start: now, end: thirtyDaysFromNow })) {
              const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              const entityName = doc.fleet_vehicles?.plate || "Veículo";
              events.push({
                id: `doc-${doc.id}`,
                title: `Vencimento ${doc.name} - ${entityName}`,
                date: expiryDate,
                time: "23:59",
                type: 'document',
                priority: daysUntilExpiry <= 7 ? 'high' : daysUntilExpiry <= 15 ? 'medium' : 'low',
                source: "fleet_documents",
                reference_id: doc.id,
                icon: "FileText"
              });
            }
          }
        });

        // 2b. Documentos da frota vencendo - motoristas
        const { data: driverDocuments } = await supabase
          .from("fleet_documents")
          .select(`
            id, 
            name, 
            expiry_date,
            driver_id,
            fleet_drivers!inner(name)
          `)
          .eq("company_id", companyId)
          .eq("status", "active")
          .eq("category", "driver")
          .not("expiry_date", "is", null);

        driverDocuments?.forEach((doc: any) => {
          if (doc.expiry_date) {
            const expiryDate = parseISO(doc.expiry_date);
            if (isWithinInterval(expiryDate, { start: now, end: thirtyDaysFromNow })) {
              const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              const entityName = doc.fleet_drivers?.name || "Motorista";
              events.push({
                id: `doc-${doc.id}`,
                title: `Vencimento ${doc.name} - ${entityName}`,
                date: expiryDate,
                time: "23:59",
                type: 'document',
                priority: daysUntilExpiry <= 7 ? 'high' : daysUntilExpiry <= 15 ? 'medium' : 'low',
                source: "fleet_documents",
                reference_id: doc.id,
                icon: "FileText"
              });
            }
          }
        });

        // 3. CNH dos motoristas vencendo
        const { data: drivers } = await supabase
          .from("fleet_drivers")
          .select("id, name, cnh_valid_until")
          .eq("company_id", companyId)
          .eq("status", "active")
          .not("cnh_valid_until", "is", null);

        drivers?.forEach((driver) => {
          if (driver.cnh_valid_until) {
            const cnhExpiry = parseISO(driver.cnh_valid_until);
            if (isWithinInterval(cnhExpiry, { start: now, end: thirtyDaysFromNow })) {
              const daysUntilExpiry = Math.ceil((cnhExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              events.push({
                id: `driver-${driver.id}`,
                title: `Vencimento CNH - ${driver.name}`,
                date: cnhExpiry,
                time: "23:59",
                type: 'driver_license',
                priority: daysUntilExpiry <= 7 ? 'high' : daysUntilExpiry <= 15 ? 'medium' : 'low',
                source: "fleet_drivers",
                reference_id: driver.id,
                icon: "User"
              });
            }
          }
        });

        // 4. Manutenções programadas
        const { data: maintenancePlans } = await supabase
          .from("fleet_maint_plans")
          .select(`
            id, 
            name, 
            next_execution_date,
            fleet_vehicles(plate)
          `)
          .eq("company_id", companyId)
          .eq("status", "active")
          .not("next_execution_date", "is", null);

        maintenancePlans?.forEach((plan) => {
          if (plan.next_execution_date) {
            const executionDate = parseISO(plan.next_execution_date);
            if (isWithinInterval(executionDate, { start: now, end: thirtyDaysFromNow })) {
              const daysUntilExecution = Math.ceil((executionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              events.push({
                id: `maintenance-${plan.id}`,
                title: `${plan.name} - ${plan.fleet_vehicles?.plate}`,
                date: executionDate,
                time: "08:00",
                type: 'maintenance',
                priority: daysUntilExecution <= 3 ? 'high' : daysUntilExecution <= 7 ? 'medium' : 'low',
                source: "fleet_maint_plans",
                reference_id: plan.id,
                icon: "Wrench"
              });
            }
          }
        });

        // 5. Tickets com SLA próximo do vencimento
        const { data: tickets } = await supabase
          .from("support_tickets")
          .select("id, title, sla_due_at, priority")
          .eq("company_id", companyId)
          .in("status", ["open", "in_progress"])
          .not("sla_due_at", "is", null);

        tickets?.forEach((ticket) => {
          if (ticket.sla_due_at) {
            const slaDate = parseISO(ticket.sla_due_at);
            if (isWithinInterval(slaDate, { start: now, end: thirtyDaysFromNow })) {
              const hoursUntilSLA = Math.ceil((slaDate.getTime() - now.getTime()) / (1000 * 60 * 60));
              events.push({
                id: `ticket-${ticket.id}`,
                title: `SLA Ticket: ${ticket.title}`,
                date: slaDate,
                time: slaDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                type: 'ticket',
                priority: hoursUntilSLA <= 4 ? 'high' : hoursUntilSLA <= 24 ? 'medium' : 'low',
                source: "support_tickets",
                reference_id: ticket.id,
                icon: "AlertCircle"
              });
            }
          }
        });

      } catch (error) {
        console.error("Error fetching upcoming events:", error);
      }

      // Ordenar por data (mais próximos primeiro) e depois por prioridade
      return events
        .sort((a, b) => {
          const dateComparison = a.date.getTime() - b.date.getTime();
          if (dateComparison !== 0) return dateComparison;
          
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        })
        .slice(0, 6); // Limitar a 6 eventos mais relevantes
    },
    enabled: !!companyId,
    refetchInterval: 5 * 60 * 1000, // Atualizar a cada 5 minutos
  });
}